<template>
  <PageFooter
    v-if="!isCheckout"
    class="page-footer-wrapper"
    :nav-footer-items="navFooterItems"
    :nav-legal-items="navLegalItems"
    :nav-legal-line-1="$globalPageSettings?.navLegalLine1 ?? ''"
    :nav-legal-line-2="$globalPageSettings?.navLegalLine2 ?? ''"
    v-bind="pageFooterForwardCompatProps"
    :nav-social="navSocialItems"
    :responsive-font-sizes="true"
    :newsletter-registration="newsletterRegistrationItems"
    :tagline="tagline"
    :trustbox="trustBoxProps"
    @submit-newsletter="onNewsletterRegistration"
  >
    <template #region-language-selector>
      <div class="theme-dark">
        <div :class="$style.regionSelectorOpenModalButton">
          <LinkElement
            style-type="primary"
            :title="regionSelectorTitle || undefined"
            :link="{
              uri: {
                path: route.path,
                query: route.query,
                hash: '#regionselector'
              }
            }"
          >
            <!-- button icon not working -->
            <Icon :class="$style.iconPadding" :scale-factor="2" name="globe" />
            {{ geoinformationStore.currentRegionLabel }}
          </LinkElement>
        </div>
      </div>
    </template>
  </PageFooter>
  <DistractionFreeFooter
    v-if="isCheckout"
    :nav-legal-items="navLegalItems"
    :copyright="`${$globalPageSettings?.navLegalLine1 ?? ''}`"
    :copyright-tagline="`${$globalPageSettings?.navLegalLine2 ?? ''}`"
    v-bind="distractionFreeFooterForwardCompatProps"
  />
</template>

<script setup lang="ts">
import {
  DistractionFreeFooter,
  Icon,
  LinkElement,
  PageFooter
} from '@hypercodestudio/basler-components';
import type {
  TrustBoxInterface,
  NavigationItemInterface,
  NewsletterRegistrationInterface,
  NavSocialProps as NavSocialInterface
} from '@hypercodestudio/basler-components';

import {
  useCreateNavItemFromMinified,
  mapMinifiedSocialLinkToNavSocialItem
} from '~/composables/useCreateMainNavItem';
import { isDefined } from '~/utils/guards/isDefined';
import isOneOfCheckoutPages from '~/utils/shop/isOneOfCheckoutPages';
import { useGeoinformationStore } from '~/stores/geoinformationStore/geoinformationStore';
import { toForwardCompatProps } from '~/utils/toForwardCompatProps';
import { useHydrationSafeDictionaryText } from '~/composables/useHydrationSafeDictionaryText';

const slug = useSlug();
const { $globalPageSettings, $textDictionary, $activeLocale } = useNuxtApp();
const route = useRoute();
const router = useRouter();
const geoinformationStore = useGeoinformationStore();

const isCheckout = computed(() =>
  isOneOfCheckoutPages($globalPageSettings, slug)
);

const {
  isEnabled: isTrustBoxEnabled,
  badge,
  trustItems,
  serviceProviderItems
} = useTrustBox();

const trustBoxProps = computed<TrustBoxInterface | undefined>(() => {
  if (!isTrustBoxEnabled.value) {
    return undefined;
  }

  return {
    variant: 'footer',
    badge: badge.value,
    trustItems: trustItems.value,
    serviceProviderItems: serviceProviderItems.value
  };
});

const navSocialItems = computed<NavSocialInterface>(() => ({
  title: $globalPageSettings.value?.socialMediaHeadline ?? '',
  text: $globalPageSettings.value?.socialMediaText ?? '',
  items: ($globalPageSettings.value?.companySocialMediaLinks?.items ?? [])
    .map((item) =>
      mapMinifiedSocialLinkToNavSocialItem(item, $activeLocale.value.webapp)
    )
    .filter(isDefined)
}));

const navFooterItems = computed<NavigationItemInterface[]>(() =>
  ($globalPageSettings.value?.footerNavigation?.items ?? [])
    .map((item) => useCreateNavItemFromMinified(item))
    .filter(isDefined)
);

const navLegalItems = computed<NavigationItemInterface[]>(() =>
  ($globalPageSettings.value?.footerLegalNavigation?.items ?? [])
    .map((item) => useCreateNavItemFromMinified(item))
    .filter(isDefined)
);

const navFooterAriaLabel = computed(
  () =>
    $textDictionary.value['pageFooter.navigation.ariaLabel'] ??
    'Footer navigation'
);
const navFooterAdditionalAriaLabel = computed(
  () =>
    $textDictionary.value['pageFooter.navigation.additional.ariaLabel'] ??
    'Additional footer navigation'
);
const navFooterAccordionToggleLabel = useHydrationSafeDictionaryText(
  'pageFooter.navigation.accordionToggle.label',
  'Toggle'
);
const regionSelectorTitle = useHydrationSafeDictionaryText(
  'pageFooter.languageSelector.chooseRegion',
  ''
);
const navLegalAriaLabel = computed(
  () =>
    $textDictionary.value['pageFooter.navigation.legal.ariaLabel'] ??
    'Legal navigation'
);
const navSocialAriaLabel = computed(
  () =>
    $textDictionary.value['pageFooter.navigation.social.ariaLabel'] ??
    'Social media'
);

const pageFooterForwardCompatProps = computed(() =>
  toForwardCompatProps({
    navFooterAriaLabel: navFooterAriaLabel.value,
    navFooterAdditionalAriaLabel: navFooterAdditionalAriaLabel.value,
    navFooterAccordionToggleLabel: navFooterAccordionToggleLabel.value,
    navLegalAriaLabel: navLegalAriaLabel.value,
    navSocialAriaLabel: navSocialAriaLabel.value
  })
);

const distractionFreeFooterForwardCompatProps = computed(() =>
  toForwardCompatProps({
    navLegalAriaLabel: navLegalAriaLabel.value
  })
);

const newsletterRegistrationItems = computed<NewsletterRegistrationInterface>(
  () => ({
    copy: $globalPageSettings.value?.newsletterCopy ?? '',
    headline: $globalPageSettings.value?.newsletterHeadline ?? '',
    placeholder: $globalPageSettings.value?.newsletterPlaceholder ?? ''
  })
);

const tagline = {
  type: 'image',
  src: '/images/the-power-of-sight.svg',
  alt: 'The Power of Sight',
  width: 236,
  height: 24
};

const newsletterRegistrationLink = $globalPageSettings.value
  ?.newsletterTargetPage?.slug
  ? buildLinkInterface(
      buildUrlString(
        $activeLocale.value.webapp,
        $globalPageSettings.value?.newsletterTargetPage?.slug
      ),
      $activeLocale.value.webapp
    )
  : null;

const onNewsletterRegistration = (email: string) => {
  const isEmail = String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
  if (
    !newsletterRegistrationLink ||
    !isEmail ||
    !newsletterRegistrationLink.uri
  ) {
    return;
  }

  router.push({
    path: newsletterRegistrationLink.uri.toString(),
    query: { email }
  });
};
</script>

<style module>
.regionSelectorOpenModalButton button {
  font-weight: 400;
  padding: 5px 13px;
}

.iconPadding {
  /* copied from hypercodes region selector */
  margin-inline-end: max(0.25em, 6px);
}
</style>
